import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Input,
  Select,
  DatePicker,
  Typography,
  Tabs,
  Badge,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ExportOutlined,
  EyeOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMaterialRequests } from '../../hooks/useMaterialRequest';
import { useMaterialIssuances } from '../../hooks/useMaterialIssuance';
import { MaterialIssuanceDialog } from '../../components/inventory/MaterialIssuanceDialog';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useWarehouses } from '../../hooks/useInventory';
import { useAuthStore } from '@/store/authStore';
import { isPlatformSuperAdmin } from '@/utils/authUser';
import type { MaterialRequest } from '../../services/materialRequest.service';
import type { MaterialIssuance } from '../../services/materialIssuance.service';

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const MaterialIssuesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatCurrency } = useCurrency();

  const [searchText, setSearchText] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [activeTab, setActiveTab] = useState<'queue' | 'register'>('queue');
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [isIssuanceDialogVisible, setIsIssuanceDialogVisible] = useState(false);

  const materialRequestIdFromUrl = searchParams.get('material_request_id') || '';
  const workOrderIdFromUrl = searchParams.get('work_order_id') || '';

  const user = useAuthStore((s) => s.user);
  const canIssue =
    isPlatformSuperAdmin(user) || user?.permissions?.includes('inventory.material_issue.create');

  useEffect(() => {
    if (materialRequestIdFromUrl && canIssue) {
      setSelectedRequestId(materialRequestIdFromUrl);
      setIsIssuanceDialogVisible(true);
      setActiveTab('queue');
    }
  }, [materialRequestIdFromUrl, canIssue]);

  const { data: warehouses = [] } = useWarehouses();

  const { data: queueRequests = [], isLoading: queueLoading } = useMaterialRequests({
    fulfillmentQueue: true,
    work_order_id: workOrderIdFromUrl || undefined,
    priority: priorityFilter || undefined,
    startDate: dateRange?.[0],
    endDate: dateRange?.[1],
  });

  const { data: issuances = [], isLoading: issuancesLoading } = useMaterialIssuances({
    startDate: dateRange?.[0],
    endDate: dateRange?.[1],
  });

  const filteredQueue = useMemo(() => {
    return queueRequests.filter((request) => {
      if (warehouseFilter && request.source_warehouse_id !== warehouseFilter) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        return (
          request.request_number.toLowerCase().includes(q) ||
          (request.work_order_number && request.work_order_number.toLowerCase().includes(q)) ||
          (request.context_label && request.context_label.toLowerCase().includes(q)) ||
          (request.project_summary?.project_number &&
            request.project_summary.project_number.toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [queueRequests, warehouseFilter, searchText]);

  const filteredIssuances = useMemo(() => {
    return issuances.filter((row) => {
      if (warehouseFilter && row.source_warehouse_id !== warehouseFilter) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        return (
          row.issuance_number.toLowerCase().includes(q) ||
          (row.materialRequest?.request_number &&
            row.materialRequest.request_number.toLowerCase().includes(q)) ||
          (row.work_order_number && row.work_order_number.toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [issuances, warehouseFilter, searchText]);

  const getPriorityColor = (priority: string) => {
    const colorMap: Record<string, string> = {
      Low: 'blue',
      Normal: 'default',
      High: 'orange',
      Urgent: 'red',
    };
    return colorMap[priority] || 'default';
  };

  const contextColumn = {
    title: 'Context',
    key: 'context',
    width: 170,
    render: (_: unknown, record: MaterialRequest) => {
      if (record.work_order_id && record.work_order_number) {
        return (
          <Link to={`/mfg/work-orders/${record.work_order_id}`}>{record.work_order_number}</Link>
        );
      }
      if (record.project_id && record.project_summary) {
        return (
          <Text>
            {record.project_summary.project_number || record.project_summary.project_name}
          </Text>
        );
      }
      return <Text type="secondary">{record.context_label || '—'}</Text>;
    },
  };

  const queueColumns: ColumnsType<MaterialRequest> = [
    {
      title: 'Request #',
      dataIndex: 'request_number',
      key: 'request_number',
      width: 140,
      fixed: 'left',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    contextColumn,
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: string) => <Tag color={getPriorityColor(priority)}>{priority}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'request_status',
      key: 'request_status',
      width: 130,
      render: (status: string) => <Tag>{status}</Tag>,
    },
    {
      title: 'Items',
      key: 'items',
      width: 70,
      render: (_: unknown, record: MaterialRequest) => record.items?.length ?? 0,
    },
    {
      title: 'Required',
      dataIndex: 'required_date',
      key: 'required_date',
      width: 110,
      render: (date: string) => (date ? dayjs(date).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Warehouse',
      dataIndex: 'source_warehouse_id',
      key: 'source_warehouse_id',
      width: 120,
      ellipsis: true,
      render: (id: string) => {
        const wh = warehouses.find((w) => w.id === id);
        return wh?.warehouse_name || wh?.warehouse_code || id || '—';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_: unknown, record: MaterialRequest) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/scm/inventory/material-requests/${record.id}`)}
          >
            View MR
          </Button>
          {canIssue ? (
            <Button
              type="primary"
              size="small"
              icon={<ExportOutlined />}
              onClick={() => {
                setSelectedRequestId(record.id);
                setIsIssuanceDialogVisible(true);
              }}
            >
              Issue
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const registerColumns: ColumnsType<MaterialIssuance> = [
    {
      title: 'Issuance #',
      dataIndex: 'issuance_number',
      key: 'issuance_number',
      width: 140,
      fixed: 'left',
      render: (text: string, record) => (
        <Link to={`/scm/inventory/material-issues/${record.id}`}>
          <Text strong>{text}</Text>
        </Link>
      ),
    },
    {
      title: 'MR #',
      key: 'material_request',
      width: 130,
      render: (_: unknown, record: MaterialIssuance) =>
        record.materialRequest?.request_number ? (
          <Link to={`/scm/inventory/material-requests/${record.material_request_id}`}>
            {record.materialRequest.request_number}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      title: 'Work Order',
      key: 'work_order',
      width: 140,
      render: (_: unknown, record: MaterialIssuance) =>
        record.work_order_number ? (
          <Link to={`/mfg/work-orders/${record.work_order_id}`}>{record.work_order_number}</Link>
        ) : (
          '—'
        ),
    },
    {
      title: 'Date',
      dataIndex: 'issuance_date',
      key: 'issuance_date',
      width: 150,
      render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY HH:mm') : '—'),
    },
    {
      title: 'Type',
      dataIndex: 'issuance_type',
      key: 'issuance_type',
      width: 90,
      render: (t: string) => <Tag>{t}</Tag>,
    },
    {
      title: 'Total cost',
      dataIndex: 'total_cost',
      key: 'total_cost',
      width: 110,
      render: (v: number | string) => {
        const n = typeof v === 'string' ? parseFloat(v) : Number(v || 0);
        return formatCurrency(Number.isFinite(n) ? n : 0);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_: unknown, record: MaterialIssuance) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/scm/inventory/material-issues/${record.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Material Issues</Title>
        <Text type="secondary">Fulfillment queue and issuance register for store operations</Text>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Search
            placeholder="Search request, issuance, WO, project"
            allowClear
            style={{ width: 280 }}
            onSearch={setSearchText}
            onChange={(e) => !e.target.value && setSearchText('')}
          />
          <Select
            placeholder="Warehouse"
            allowClear
            style={{ width: 180 }}
            onChange={(v) => setWarehouseFilter(v || '')}
            options={warehouses.map((w) => ({
              value: w.id,
              label: w.warehouse_name || w.warehouse_code,
            }))}
          />
          {activeTab === 'queue' ? (
            <Select
              placeholder="Priority"
              allowClear
              style={{ width: 140 }}
              onChange={(v) => setPriorityFilter(v || '')}
            >
              <Select.Option value="Low">Low</Select.Option>
              <Select.Option value="Normal">Normal</Select.Option>
              <Select.Option value="High">High</Select.Option>
              <Select.Option value="Urgent">Urgent</Select.Option>
            </Select>
          ) : null}
          <RangePicker
            onChange={(dates) => {
              if (dates) {
                setDateRange([
                  dates[0]?.format('YYYY-MM-DD') || '',
                  dates[1]?.format('YYYY-MM-DD') || '',
                ]);
              } else {
                setDateRange(null);
              }
            }}
          />
          <Button icon={<FilterOutlined />} onClick={() => {
            setSearchText('');
            setWarehouseFilter('');
            setPriorityFilter('');
            setDateRange(null);
          }}>
            Clear
          </Button>
        </Space>
      </Card>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as 'queue' | 'register')}
          items={[
            {
              key: 'queue',
              label: (
                <Badge count={filteredQueue.length} offset={[10, 0]}>
                  <span>Ready to issue</span>
                </Badge>
              ),
            },
            {
              key: 'register',
              label: <span>Issuance register</span>,
            },
          ]}
        />

        {activeTab === 'queue' ? (
          <Table
            columns={queueColumns}
            dataSource={filteredQueue}
            rowKey="id"
            loading={queueLoading}
            scroll={{ x: 1200 }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} requests ready`,
            }}
          />
        ) : (
          <Table
            columns={registerColumns}
            dataSource={filteredIssuances}
            rowKey="id"
            loading={issuancesLoading}
            scroll={{ x: 1100 }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} issuances`,
            }}
          />
        )}
      </Card>

      <MaterialIssuanceDialog
        visible={isIssuanceDialogVisible}
        materialRequestId={selectedRequestId}
        onClose={() => {
          setIsIssuanceDialogVisible(false);
          setSelectedRequestId(null);
          if (materialRequestIdFromUrl) {
            searchParams.delete('material_request_id');
            setSearchParams(searchParams, { replace: true });
          }
        }}
        onSuccess={() => {
          setIsIssuanceDialogVisible(false);
          setSelectedRequestId(null);
          if (materialRequestIdFromUrl) {
            searchParams.delete('material_request_id');
            setSearchParams(searchParams, { replace: true });
          }
        }}
      />
    </div>
  );
};

export default MaterialIssuesPage;
